/**
 * One catalog per app, reachable from any widget.
 *
 * A context rather than a hook per widget: a kit form has an item grid with a dozen tiles and
 * every one of them wants the same 2,000-entry list. Loading it once and handing out map
 * reads is the difference between one fetch and twelve, and — more importantly — it is what
 * lets `BlockProps` stay exactly as F1 defined it. No widget takes a registry prop, nothing
 * threads a catalog through `BlockSchemaField`, and the block registry did not have to learn
 * that item widgets are special.
 *
 * The default value is a LOADING catalog rather than `undefined`, so a widget rendered
 * outside the provider (a unit test, a future route) gets "no suggestions yet" instead of a
 * crash. That is the same rule the rest of this track follows: a missing artifact degrades
 * the editor, it never breaks it.
 */
import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { emptyCatalog, loadCatalog, resolveArtifactBase, type RegistryCatalog } from './dump';

const RegistryContext = createContext<RegistryCatalog>(emptyCatalog('loading'));

export function useRegistry(): RegistryCatalog {
  return useContext(RegistryContext);
}

/**
 * The entries a field should offer, from the field's own `x-enum-source`.
 *
 * This is the entire coupling between a schema and the registry data, and it is one string
 * lookup. A widget calls it; nothing registers anything.
 */
export function useRegistryEntries(source: string | undefined): ReturnType<RegistryCatalog['entriesFor']> {
  const catalog = useRegistry();
  return catalog.entriesFor(source);
}

export interface RegistryProviderProps {
  readonly children: React.ReactNode;
  /** Injected by tests; production resolves it from the build-time environment. */
  readonly catalog?: RegistryCatalog;
  readonly base?: string;
  readonly fetchImpl?: typeof fetch;
}

export function RegistryProvider({ children, catalog, base, fetchImpl }: RegistryProviderProps): React.JSX.Element {
  const resolvedBase = useMemo(
    () => base ?? resolveArtifactBase(import.meta.env as unknown as Record<string, string | boolean | undefined>),
    [base],
  );
  const [loaded, setLoaded] = useState<RegistryCatalog>(emptyCatalog('loading'));

  useEffect(() => {
    if (catalog !== undefined) return undefined;
    const controller = new AbortController();
    loadCatalog({ base: resolvedBase, ...(fetchImpl === undefined ? {} : { fetchImpl }), signal: controller.signal })
      .then((next) => {
        if (!controller.signal.aborted) setLoaded(next);
      })
      .catch(() => {
        // loadCatalog does not reject; this is belt and braces so a future edit to it cannot
        // leave the whole editor stuck on a spinner.
        if (!controller.signal.aborted) setLoaded(emptyCatalog('unavailable', 'The registry dump could not be loaded.'));
      });
    return () => controller.abort();
  }, [catalog, resolvedBase, fetchImpl]);

  const value = catalog ?? loaded;
  return <RegistryContext.Provider value={value}>{children}</RegistryContext.Provider>;
}

/**
 * The one visible statement of where the suggestions came from.
 *
 * Rendered by the schema-form page rather than by each widget: an admin needs to know ONCE
 * that this editor is talking to a modpack with 2,039 items, and needs to be told ONCE — with
 * the reason — when it is not. A per-field "no suggestions" notice on forty fields would be
 * noise, and its absence would be a mystery.
 */
export function RegistryStatus(): React.JSX.Element | null {
  const catalog = useRegistry();
  if (catalog.state === 'loading') return null;

  if (catalog.state === 'unavailable') {
    return (
      <p className="dc-registry-status dc-field-hint dc-field-warn" data-testid="registry-status" data-registry-state="unavailable">
        No id suggestions: {catalog.reason} Every field below still accepts a typed id.
      </p>
    );
  }

  const total = catalog.sources.reduce((sum, source) => sum + catalog.entriesFor(source).length, 0);
  return (
    <p className="dc-registry-status dc-field-hint" data-testid="registry-status" data-registry-state="ready" data-registry-entries={total}>
      Suggestions from this server’s registries: {total.toLocaleString()} entries across {catalog.sources.length} registries
      {catalog.modVersion === '' ? '' : ` (mod ${catalog.modVersion})`}.
    </p>
  );
}
